import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { NabooError } from '@/core/infrastructure/services/NabooError.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import { FetchQuizUseCase } from '@/core/useCases/quiz/FetchQuizUseCase.ts'
import { UpdateQuizQuestionsUseCase } from '@/core/useCases/quiz/UpdateQuizQuestionsUseCase.ts'
import { UpdateQuizVersionUseCase } from '@/core/useCases/quiz/UpdateQuizVersionUseCase.ts'
import { MarkQuizAsReadyUseCase } from '@/core/useCases/quiz/MarkQuizAsReadyUseCase.ts'
import {
  CreateQuizVersionUseCase,
  type CreateQuizVersionPayload
} from '@/core/useCases/quiz/CreateQuizVersionUseCase.ts'
import { ActivateQuizVersionUseCase } from '@/core/useCases/quiz/ActivateQuizVersionUseCase.ts'
import { ArchiveQuizVersionUseCase } from '@/core/useCases/quiz/ArchiveQuizVersionUseCase.ts'
import { DeleteQuizVersionUseCase } from '@/core/useCases/quiz/DeleteQuizVersionUseCase.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'

export const useQuizStore = defineStore('quiz', () => {
  // DEPENDENCY INJECTION
  const fetchQuizUseCase = DependencyContainer.resolve(FetchQuizUseCase)
  const updateQuizQuestionsUseCase = DependencyContainer.resolve(UpdateQuizQuestionsUseCase)
  const updateQuizVersionUseCase = DependencyContainer.resolve(UpdateQuizVersionUseCase)
  const markLearningQuizAsReadyUseCase = DependencyContainer.resolve(MarkQuizAsReadyUseCase)
  const createQuizVersionUseCase = DependencyContainer.resolve(CreateQuizVersionUseCase)
  const activateQuizVersionUseCase = DependencyContainer.resolve(ActivateQuizVersionUseCase)
  const archiveQuizVersionUseCase = DependencyContainer.resolve(ArchiveQuizVersionUseCase)
  const deleteQuizVersionUseCase = DependencyContainer.resolve(DeleteQuizVersionUseCase)

  // STATE
  const currentQuiz: Ref<Quiz | null> = ref(null)

  // Utilisation de la base commune pour les stores
  const storeBase = createStoreBase([
    'fetchQuiz',
    'updateQuiz',
    'updateVersion',
    'markQuizAsReady',
    'createVersion',
    'activateVersion',
    'archiveVersion',
    'deleteVersion'
  ])

  // ACTIONS

  /**
   * Réinitialise le quiz actuel.
   */
  function clearCurrentQuiz() {
    currentQuiz.value = null
  }

  /**
   * Récupère un quiz spécifique.
   * @param quizId - L'ID du quiz à récupérer.
   * @returns Le quiz récupéré.
   */
  async function fetchQuizById(quizId: number) {
    console.log('fetchQuiz', quizId)
    try {
      storeBase.loading.value.fetchQuiz = true
      storeBase.resetError()

      // Réinitialiser le quiz actuel avant de charger le nouveau
      // pour éviter d'afficher l'ancien quiz pendant le chargement
      currentQuiz.value = null

      const quiz = await fetchQuizUseCase.execute(quizId)

      currentQuiz.value = quiz
      storeBase.updateLastUpdated()

      return quiz
    } catch (error) {
      handleError(error, 'Erreur lors de la récupération du quiz')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.fetchQuiz = false
    }
  }

  /**
   * Met à jour les questions d'un quiz.
   * @param quizId - L'ID du quiz à mettre à jour.
   * @param questions - Les nouvelles questions du quiz.
   * @returns Le quiz mis à jour.
   */
  async function updateQuizQuestions(quizId: number, questions: QuizQuestion[]) {
    try {
      storeBase.loading.value.updateQuiz = true
      storeBase.resetError()

      const updatedQuiz = await updateQuizQuestionsUseCase.execute(quizId, questions)

      currentQuiz.value = updatedQuiz

      storeBase.showSuccessToast(
        'Quiz mis à jour avec succès',
        'Les questions du quiz ont été mises à jour avec succès.'
      )
      storeBase.updateLastUpdated()

      // Note: Les notifications aux stores sont maintenant gérées par le composant VQuizEdit
      // selon le contexte (taskId ou grainId) plutôt que par le type de quiz

      return updatedQuiz
    } catch (error) {
      handleError(error, 'Erreur lors de la mise à jour des questions du quiz')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.updateQuiz = false
    }
  }

  /**
   * Met à jour une version spécifique d'un quiz.
   * @param quizId - L'ID du quiz à mettre à jour.
   * @param version - La version complète à mettre à jour.
   * @returns Le quiz mis à jour.
   */
  async function updateQuizVersion(quizId: number, version: QuizVersion) {
    try {
      storeBase.loading.value.updateVersion = true
      storeBase.resetError()

      const updatedQuiz = await updateQuizVersionUseCase.execute(quizId, version)

      currentQuiz.value = updatedQuiz

      storeBase.showSuccessToast(
        'Version mise à jour',
        'La version du quiz a été mise à jour avec succès.'
      )
      storeBase.updateLastUpdated()

      // Note: Les notifications aux stores sont maintenant gérées par le composant VQuizEdit
      // selon le contexte (taskId ou grainId) plutôt que par le type de quiz

      return updatedQuiz
    } catch (error) {
      handleError(error, 'Erreur lors de la mise à jour de la version')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.updateVersion = false
    }
  }

  /**
   * Marque un quiz comme prêt.
   * @param quizId - L'ID du quiz à marquer comme prêt.
   * @returns Le quiz mis à jour.
   */
  async function markQuizAsReady(quizId: number) {
    try {
      storeBase.loading.value.markQuizAsReady = true
      storeBase.resetError()

      const updatedQuiz = await markLearningQuizAsReadyUseCase.execute(quizId)

      currentQuiz.value = updatedQuiz
      storeBase.updateLastUpdated()

      return updatedQuiz
    } catch (error) {
      handleError(error, 'Erreur lors de la mise à jour du quiz')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.markQuizAsReady = false
    }
  }

  /**
   * Crée une nouvelle version d'un quiz.
   * @param quizId - L'ID du quiz.
   * @param payload - Les données de la nouvelle version.
   * @returns La nouvelle version créée.
   */
  async function createQuizVersion(quizId: number, payload: CreateQuizVersionPayload) {
    try {
      storeBase.loading.value.createVersion = true
      storeBase.resetError()

      const newVersion = await createQuizVersionUseCase.execute(quizId, payload)

      // Récupérer le quiz complet mis à jour
      const updatedQuiz = await fetchQuizUseCase.execute(quizId)
      currentQuiz.value = updatedQuiz

      storeBase.showSuccessToast(
        'Nouvelle version créée',
        'Une nouvelle version du quiz a été créée avec succès.'
      )
      storeBase.updateLastUpdated()

      return newVersion
    } catch (error) {
      handleError(error, 'Erreur lors de la création de la version')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.createVersion = false
    }
  }

  /**
   * Active une version spécifique d'un quiz.
   * @param quizId - L'ID du quiz.
   * @param versionId - L'ID de la version à activer.
   * @returns Le quiz mis à jour.
   */
  async function activateQuizVersion(quizId: number, versionId: number) {
    try {
      storeBase.loading.value.activateVersion = true
      storeBase.resetError()

      // Vérifier que la version peut être activée
      if (currentQuiz.value) {
        const version = currentQuiz.value.findVersionById(versionId)
        if (version && version.isDraft) {
          // Vérifier que la version a au moins 5 questions valides
          const validQuestions = version.questions.filter((question) => question.isValid()).length
          const requiredQuestions = 5

          if (validQuestions < requiredQuestions) {
            const missing = requiredQuestions - validQuestions
            const errorMessage = `Cette version ne peut pas être activée car elle n'a que ${validQuestions} question${validQuestions > 1 ? 's' : ''} valide${validQuestions > 1 ? 's' : ''} sur les ${requiredQuestions} requises (${missing} manquante${missing > 1 ? 's' : ''})`

            storeBase.showErrorToast('Version non activable', errorMessage)

            return Promise.reject(new Error(errorMessage))
          }
        }
      }

      const updatedQuiz = await activateQuizVersionUseCase.execute(quizId, versionId)

      currentQuiz.value = updatedQuiz
      storeBase.showSuccessToast('Version activée', 'La version du quiz a été activée avec succès.')
      storeBase.updateLastUpdated()

      // Note: Les notifications aux stores sont maintenant gérées par le composant VQuizEdit
      // selon le contexte (taskId ou grainId) plutôt que par le type de quiz

      return updatedQuiz
    } catch (error) {
      handleError(error, "Erreur lors de l'activation de la version")
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.activateVersion = false
    }
  }

  /**
   * Archive une version spécifique d'un quiz.
   * @param quizId - L'ID du quiz.
   * @param versionId - L'ID de la version à archiver.
   * @returns Le quiz mis à jour.
   */
  async function archiveQuizVersion(quizId: number, versionId: number) {
    try {
      storeBase.loading.value.archiveVersion = true
      storeBase.resetError()

      const updatedQuiz = await archiveQuizVersionUseCase.execute(quizId, versionId)

      currentQuiz.value = updatedQuiz
      storeBase.showSuccessToast(
        'Version archivée',
        'La version du quiz a été archivée avec succès.'
      )
      storeBase.updateLastUpdated()

      return updatedQuiz
    } catch (error) {
      handleError(error, "Erreur lors de l'archivage de la version")
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.archiveVersion = false
    }
  }

  /**
   * Supprime une version brouillon d'un quiz.
   * @param quizId - L'ID du quiz.
   * @param versionId - L'ID de la version à supprimer.
   * @returns Le quiz mis à jour.
   */
  async function deleteQuizVersion(quizId: number, versionId: number) {
    try {
      storeBase.loading.value.deleteVersion = true
      storeBase.resetError()

      const updatedQuiz = await deleteQuizVersionUseCase.execute(quizId, versionId)

      currentQuiz.value = updatedQuiz
      storeBase.showSuccessToast(
        'Version supprimée',
        'La version du quiz a été supprimée avec succès.'
      )
      storeBase.updateLastUpdated()

      return updatedQuiz
    } catch (error) {
      handleError(error, 'Erreur lors de la suppression de la version')
      return Promise.reject(error)
    } finally {
      storeBase.loading.value.deleteVersion = false
    }
  }

  // Helper pour gérer les erreurs
  function handleError(error: unknown, defaultMessage: string) {
    let nabooError: NabooError

    if (error instanceof NabooError) {
      nabooError = error
    } else if (error instanceof Error) {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'LearningQuizStoreError',
        code: 'LEARNING_QUIZ_STORE_ERROR',
        originalError: error
      })
    } else {
      nabooError = new NabooError({
        message: defaultMessage,
        status: 500,
        name: 'LearningQuizStoreError',
        code: 'LEARNING_Quiz_STORE_ERROR'
      })
    }

    storeBase.setError(nabooError)
    storeBase.showErrorToast(
      nabooError.message,
      'Une erreur est survenue lors de la mise à jour du quiz.'
    )
  }

  return {
    // State
    ...storeBase,
    currentQuiz,

    // Actions
    clearCurrentQuiz,
    fetchQuizById,
    updateQuizQuestions,
    updateQuizVersion,
    markQuizAsReady,
    createQuizVersion,
    activateQuizVersion,
    archiveQuizVersion,
    deleteQuizVersion
  }
})
